export const fr = {
  // Navigation
  navigation: {
    singleVerification: 'Vérification Unique',
    batchVerification: 'Vérification par Lot',
    about: 'À Propos',
    logout: 'Déconnexion'
  },
  auth: {
    emailVerification: {
      title: 'Vérifiez Votre Email',
      message: 'Nous avons envoyé un email de vérification à {email}. Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification pour activer votre compte.',
      checkEmail: 'Vérifiez votre email',
      followInstructions: 'Suivez les instructions dans l\'email pour vérifier votre compte.',
      resendEmail: 'Renvoyer l\'Email de Vérification',
      sending: 'Envoi en cours...',
      backToLogin: 'Retour à la Connexion',
      emailSent: 'Email de vérification envoyé avec succès !',
      helpText: 'Si vous ne voyez pas l\'email dans votre boîte de réception, veuillez vérifier votre dossier spam.'
    }
  },

  // Header
  header: {
    title: 'Vérification TVA Peppol',
    subtitle: 'Outil de Vérification TVA UE & Peppol'
  },

  // Footer
  footer: {
    description: 'Outil de Vérification TVA Peppol - Construit avec React & TypeScript',
    disclaimer: 'À des fins de démonstration uniquement. L\'utilisation en production nécessite une intégration API appropriée.',
    termsOfUse: 'Conditions d\'Utilisation',
    privacyPolicy: 'Politique de Confidentialité'
  },

  // About Page
  about: {
    title: 'À Propos de la Vérification TVA Peppol',
    overview: {
      title: 'Aperçu',
      content: 'Cette application fournit des services de vérification pour les numéros de TVA UE et les identifiants de participants Peppol. Elle prend en charge à la fois la vérification d\'une seule entreprise et le traitement par lot pour plusieurs entrées.'
    },
    features: {
      title: 'Fonctionnalités',
      items: [
        { label: 'Vérification TVA UE:', description: 'Valider les numéros de TVA en utilisant VIES (Système d\'Échange d\'Informations sur la TVA)' },
        { label: 'Vérification Peppol:', description: 'Vérifier les identifiants de participants dans le répertoire Peppol' },
        { label: 'Vérification Double:', description: 'Exécuter à la fois la vérification TVA et Peppol avec un seul identifiant' },
        { label: 'Vérification Unique:', description: 'Vérifier des entreprises individuelles avec des résultats détaillés' },
        { label: 'Traitement par Lot:', description: 'Télécharger des fichiers CSV pour la vérification en masse' },
        { label: 'Intégration API Réelle:', description: 'Basculer entre les appels API réels et le mode simulation' },
        { label: 'Exportation des Résultats:', description: 'Télécharger les résultats de vérification sous forme de fichiers CSV' },
        { label: 'Téléchargement de Modèles:', description: 'Obtenir des modèles CSV pour un formatage approprié des données' }
      ]
    },
    vatFormats: {
      title: 'Formats de Numéros de TVA Supportés',
      description: 'L\'application prend en charge les numéros de TVA de tous les États membres de l\'UE:',
      examples: [
        'Autriche: U12345678',
        'Belgique: 1234567890',
        'Allemagne: 123456789',
        'France: AB123456789',
        'Italie: 12345678901',
        'Pays-Bas: 123456789B01',
        'Espagne: A12345678',
        'Et bien d\'autres...'
      ]
    },
    peppolTypes: {
      title: 'Types d\'Identifiants de Participants Peppol',
      items: [
        { type: 'DUNS:', description: 'Système de Numérotation Universel de Données à 9 chiffres' },
        { type: 'GLN:', description: 'Numéro de Localisation Global à 12-15 chiffres' },
        { type: 'TVA:', description: 'Code pays + numéro de TVA' },
        { type: 'Personnalisé:', description: 'Identifiants spécifiques à l\'organisation' }
      ]
    },
    apiMode: {
      title: 'Commutation du Mode API',
      description: 'L\'application inclut un commutateur pour choisir entre:',
      items: [
        { mode: 'Mode Simulation:', description: 'Utilise des données fictives à des fins de démonstration' },
        { mode: 'Mode API Réelle:', description: 'Effectue des appels réels aux APIs VIES et Peppol' }
      ]
    },
    importantNotes: {
      title: 'Notes Importantes',
      items: [
        'Le mode API réelle nécessite un accès réseau approprié aux services VIES et Peppol',
        'Certaines APIs peuvent avoir des limites de taux ou nécessiter une authentification',
        'Le mode simulation est recommandé pour les tests et la démonstration',
        'L\'utilisation en production devrait inclure une gestion d\'erreur appropriée et une logique de nouvelle tentative'
      ]
    },
    technicalDetails: {
      title: 'Détails Techniques',
      content: 'Construit avec React, TypeScript et Tailwind CSS. Utilise Papa Parse pour le traitement CSV et inclut une gestion d\'erreur complète et des commentaires utilisateur.'
    }
  },

  // Terms of Use
  termsOfUse: {
    title: 'Conditions d\'Utilisation – vateu.eu',
    sections: {
      scope: {
        title: '1. Portée et Statut Bêta',
        content: 'Le Service est fourni gratuitement à des fins d\'évaluation uniquement. Il est actuellement offert à des testeurs sélectionnés, y compris Shell, pour évaluer la qualité et l\'efficacité avant toute libération commerciale potentielle. Aucune utilisation en production n\'est recommandée. Le Service peut être modifié, suspendu ou interrompu à tout moment sans préavis.'
      },
      dataSources: {
        title: '2. Sources de Données',
        content: 'Les validations TVA sont effectuées en utilisant le système VIES de la Commission européenne et les bases de données nationales connectées. Les validations Peppol sont basées sur des sources publiques du Répertoire Peppol / SMP. Nous ne sommes pas affiliés à la Commission européenne, à aucune autorité fiscale nationale ou à OpenPeppol.'
      },
      accuracy: {
        title: '3. Précision et Disponibilité',
        content: 'Les informations peuvent être retardées, incomplètes ou indisponibles en raison de pannes de systèmes tiers. Le Service est fourni "tel quel", sans garanties d\'aucune sorte. Aucun droit ou obligation ne peut être dérivé des résultats de validation.'
      },
      acceptableUse: {
        title: '4. Utilisation Acceptable',
        content: 'Les utilisateurs ne doivent pas abuser du Service par des demandes automatisées excessives. Les requêtes en masse sont soumises à des limites de taux et peuvent être bloquées. Le Service ne doit pas être utilisé à des fins illégales.'
      },
      intellectualProperty: {
        title: '5. Propriété Intellectuelle',
        content: 'Le contenu, le code et la conception de vateu.eu sont la propriété de Mariusz Pietruszka (PL6572490600) "VIES" et "Peppol" sont des noms de tiers; nous ne revendiquons aucun droit sur eux et les utilisons de manière descriptive uniquement. N\'utilisez pas l\'emblème de l\'UE ou les logos Peppol en relation avec ce Service.'
      },
      privacy: {
        title: '6. Confidentialité et Gestion des Données',
        content: 'Le Service ne stocke pas de manière permanente les numéros de TVA, les identifiants Peppol ou les résultats de validation soumis par les utilisateurs. Les données d\'entrée sont transmises uniquement aux systèmes de validation tiers pertinents (par exemple VIES, Répertoire Peppol) et traitées uniquement pour générer le résultat de validation affiché. Après le retour du résultat, aucune copie des données d\'entrée ou de sortie de validation n\'est conservée dans notre système, sauf pour les journaux techniques à court terme (voir Politique de Confidentialité).'
      },
      liability: {
        title: '7. Responsabilité',
        content: 'Dans toute la mesure permise par la loi, nous renonçons à toute garantie, expresse ou implicite. Nous n\'acceptons aucune responsabilité pour les dommages directs, indirects ou consécutifs résultant de l\'utilisation du Service.'
      },
      changes: {
        title: '8. Modifications',
        content: 'Nous pouvons mettre à jour ces Conditions à tout moment. L\'utilisation continue constitue une acceptation des Conditions révisées.'
      },
      governingLaw: {
        title: '9. Droit Applicable',
        content: 'Ces Conditions sont régies par les lois de la Pologne. Tout différend sera soumis à la juridiction exclusive des tribunaux de Pologne.',
        contact: 'Contact: Mariusz Pietruszka – PL6572490600'
      }
    }
  },

  // Privacy Policy
  privacyPolicy: {
    title: 'Politique de Confidentialité – vateu.eu',
    effectiveDate: 'Date d\'entrée en vigueur: 14/09/2025',
    sections: {
      controller: {
        title: '1. Contrôleur',
        content: 'Le Service est exploité par: [Nom de votre entreprise, adresse enregistrée, email de contact] Pour les demandes de confidentialité, contactez: [privacy@vateu.eu].'
      },
      dataWeProcess: {
        title: '2. Données que Nous Traitons',
        inputData: 'Données d\'entrée: les numéros de TVA et identifiants Peppol que vous soumettez pour validation sont traités uniquement en mémoire et transmis à VIES ou au Répertoire Peppol. Nous ne stockons pas ces identifiants ou les résultats de validation.',
        logs: 'Journaux: des données techniques minimales (adresse IP, type de navigateur, horodatage, journaux d\'erreurs) peuvent être collectées pour assurer la sécurité et résoudre les problèmes.',
        optionalContact: 'Contact optionnel: si vous nous contactez par email ou formulaire, nous traitons vos coordonnées et votre message.'
      },
      purposeAndLegalBasis: {
        title: '3. Finalité et Base Légale',
        items: [
          'Pour fournir des résultats de validation (notre intérêt légitime, Art. 6(1)(f) RGPD).',
          'Pour prévenir les abus et assurer la stabilité du système (intérêt légitime).',
          'Pour communiquer avec vous si vous nous contactez (exécution d\'un contrat, Art. 6(1)(b) RGPD).'
        ]
      },
      dataRetention: {
        title: '4. Rétention des Données',
        items: [
          'Les identifiants TVA/Peppol et résultats de validation ne sont pas stockés après la completion de la demande de validation.',
          'Les journaux sont conservés pendant un maximum de [par ex. 30 jours] puis supprimés ou anonymisés.',
          'Les données de contact sont conservées aussi longtemps que nécessaire pour répondre à votre demande.'
        ]
      },
      dataSharing: {
        title: '5. Partage de Données',
        items: [
          'Les demandes de validation sont transmises aux systèmes VIES (Commission européenne) et/ou Répertoire Peppol.',
          'Nous ne vendons ni ne partageons de données personnelles avec des tiers à des fins marketing.',
          'Les fournisseurs d\'hébergement et d\'infrastructure peuvent traiter des données pour notre compte sous des accords de traitement de données.'
        ]
      },
      internationalTransfers: {
        title: '6. Transferts Internationaux',
        content: 'Les données peuvent être traitées sur des serveurs situés dans l\'UE/EEE. Si des transferts en dehors de l\'UE/EEE se produisent, nous nous assurons de garanties appropriées (par exemple Clauses Contractuelles Standard).'
      },
      yourRights: {
        title: '7. Vos Droits',
        description: 'Vous avez des droits sous le RGPD, notamment:',
        items: [
          'Accès à vos données',
          'Rectification ou effacement',
          'Restriction ou objection au traitement',
          'Portabilité des données',
          'Dépôt d\'une plainte auprès de votre autorité de contrôle'
        ]
      },
      cookiesAnalytics: {
        title: '8. Cookies et Analytiques',
        content: 'Nous utilisons uniquement des cookies essentiels pour la fonctionnalité du service. Si nous introduisons des analytiques ou des cookies non essentiels, nous vous demanderons votre consentement.'
      },
      changes: {
        title: '9. Modifications',
        content: 'Nous pouvons mettre à jour cette Politique de Confidentialité. La dernière version sera toujours disponible sur le site.'
      },
      contact: {
        title: '10. Contact',
        content: 'Pour des questions ou demandes concernant cette Politique de Confidentialité, envoyez un email à [privacy@vateu.eu].'
      }
    }
  },

  // Single Verification
  singleVerification: {
    title: 'Vérification Unique',
    verificationType: 'Type de Vérification',
    types: {
      vat: 'Vérification TVA UE',
      peppol: 'Vérification Peppol',
      both: 'Les Deux Vérifications'
    },
    forms: {
      vatNumber: 'Numéro de TVA',
      vatNumberPlaceholder: 'ex: FR12345678901',
      peppolId: 'ID Participant Peppol',
      peppolIdPlaceholder: 'ex: 123456789 (DUNS) ou 1234567890123 (GLN)',
      peppolIdHelp: 'Entrez un numéro DUNS, GLN, numéro de TVA, ou identifiant participant personnalisé',
      companyIdentifier: 'Identifiant d\'Entreprise',
      companyIdentifierPlaceholder: 'ex: FR12345678901, 123456789, ou 1234567890123',
      companyIdentifierHelp: 'Entrez un numéro de TVA (FR12345678901), DUNS (123456789), GLN (1234567890123), ou tout autre identifiant',
      unifiedTitle: 'Vérification Unifiée',
      unifiedDescription: 'Entrez un seul identifiant qui sera utilisé pour les vérifications TVA et Peppol. Cela peut être un numéro de TVA, DUNS, GLN, ou tout autre identifiant d\'entreprise.'
    },
    buttons: {
      verify: 'Vérifier',
      verifying: 'Vérification...',
      reset: 'Réinitialiser'
    },
    apiMode: {
      simulationMode: 'Mode Simulation',
      realAPI: 'API Réelle',
      simulation: 'Simulation'
    },
    results: {
      verificationSuccessful: 'Vérification Réussie',
      verificationFailed: 'Vérification Échouée',
      verificationResults: 'Résultats de Vérification',
      vatVerification: 'Vérification TVA',
      peppolVerification: 'Vérification Peppol',
      fields: {
        vatNumber: 'Numéro de TVA',
        companyName: 'Nom de l\'Entreprise',
        address: 'Adresse',
        fullParticipantId: 'ID Participant Complet',
        participantName: 'Nom du Participant',
        participantId: 'ID Participant',
        scheme: 'Schéma',
        supportedDocumentTypes: 'Types de Documents Supportés',
        supportsBilling: 'Supporte la Facturation',
        error: 'Erreur',
        verifiedAt: 'Vérifié à',
        yes: 'Oui',
        no: 'Non'
      },
      multipleMatches: {
        title: 'Correspondances Multiples Trouvées',
        matchCount: 'correspondances',
        match: 'Correspondance'
      }
    }
  },

  // Batch Verification
  batchVerification: {
    title: 'Vérification par Lot',
    inputMethod: 'Méthode de Saisie',
    methods: {
      csv: 'Téléchargement CSV',
      textarea: 'Saisie Texte'
    },
    forms: {
      uploadFile: 'Télécharger Fichier CSV',
      downloadTemplate: 'Télécharger Modèle CSV',
      textAreaPlaceholder: 'Entrez les identifiants séparés par des virgules ou nouvelles lignes (ex: FR12345678901, 123456789, 1234567890123)',
      textAreaHelp: 'Entrez un ou plusieurs identifiants par ligne. Séparez plusieurs identifiants sur la même ligne avec des virgules.',
      textInputTitle: 'Pour la saisie de texte, entrez les identifiants séparés par des virgules :',
      textInputDescription: 'Entrez les identifiants séparés par des virgules. Chaque ligne peut contenir plusieurs identifiants. Prend en charge les numéros de TVA, DUNS, GLN et autres identifiants d\'entreprise.',
      textInputRules: [
        'Chaque ligne peut contenir plusieurs identifiants séparés par des virgules',
        'Prend en charge les numéros de TVA (FR12345678901), DUNS (123456789), GLN (1234567890123)',
        'Les lignes vides sont ignorées',
        'Les espaces autour des identifiants sont automatiquement supprimés'
      ],
      dualVerificationHelp: 'L\'identifiant peut être un numéro de TVA, DUNS, GLN, ou tout autre identifiant d\'entreprise qui fonctionne pour les vérifications TVA et Peppol.',
      identifiersReady: 'identifiants prêts pour la vérification',
      exportResults: 'Exporter les résultats',
      textInputPlaceholder: 'Entrez les identifiants séparés par des virgules. Chaque ligne peut contenir plusieurs identifiants :\n\nFR12345678901, DE123456789\n123456789, 987654321\n1234567890123',
      textInputHelp: 'Entrez les identifiants séparés par des virgules. Chaque ligne peut contenir plusieurs identifiants. Prend en charge les numéros de TVA, DUNS, GLN et autres identifiants d\'entreprise.'
    },
    buttons: {
      process: 'Traiter la Vérification',
      processing: 'Traitement...',
      downloadResults: 'Télécharger Résultats',
      reset: 'Réinitialiser',
      cancel: 'Annuler'
    },
    results: {
      summary: 'Résumé de Vérification',
      totalProcessed: 'Total Traité',
      valid: 'Valide',
      invalid: 'Invalide',
      errors: 'Erreurs',
      processingTime: 'Temps de Traitement',
      noResults: 'Aucun résultat à afficher',
      exportResults: 'Exporter Résultats',
      vatVerification: 'Vérification TVA',
      dualVerificationResults: 'Résultats de Vérification Double'
    },
    progress: {
      processing: 'Traitement de la vérification...',
      completed: 'Traitement terminé'
    },
    format: {
      csvRequirements: 'Exigences de Format CSV',
      textInputFormat: 'Format de Saisie Texte',
      dualVerificationTitle: 'Pour la vérification double, incluez ces colonnes :',
      dualVerificationColumns: [
        'identifier ou id - Identifiant d\'entreprise (requis)',
        'Noms de colonnes alternatifs : vatNumber, vat, taxId, participantId, peppolId'
      ],
      vatVerificationTitle: 'Pour la vérification TVA, incluez ces colonnes :',
      vatVerificationColumns: [
        'vatNumber ou vat ou taxId - Numéro de TVA (requis)'
      ],
      peppolVerificationTitle: 'Pour la vérification Peppol, incluez ces colonnes :',
      peppolVerificationColumns: [
        'participantId ou peppolId ou id - ID Participant (requis)'
      ]
    }
  },

  // Error Messages
  errors: {
    general: {
      unknownError: 'Erreur inconnue survenue',
      networkError: 'Erreur réseau survenue',
      parsingError: 'Erreur lors de l\'analyse du fichier CSV. Veuillez vérifier le format.',
      noDataToProcess: 'Veuillez télécharger un fichier CSV ou entrer des identifiants dans la zone de texte.',
      invalidInput: 'Saisie invalide fournie'
    },
    vat: {
      invalidFormat: 'Format de numéro de TVA invalide',
      countryNotSupported: 'Pays non supporté par VIES',
      serviceUnavailable: 'Service VIES actuellement indisponible',
      timeout: 'Délai d\'attente dépassé - le service VIES peut être lent',
      rateLimit: 'Limite de taux dépassée - veuillez réessayer plus tard'
    },
    peppol: {
      invalidFormat: 'Format d\'ID participant Peppol invalide',
      serviceUnavailable: 'Service Peppol actuellement indisponible',
      timeout: 'Délai d\'attente dépassé - le service Peppol peut être lent',
      notFound: 'Participant non trouvé dans le répertoire Peppol',
      rateLimit: 'Limite de taux dépassée - veuillez réessayer plus tard'
    },
    serviceUnavailable: {
      title: 'Service Indisponible',
      message: 'Le service {service} est actuellement indisponible en raison d\'une forte demande ou de maintenance. Nous réessaierons automatiquement votre requête.',
      autoRetryIn: 'Nouvelle tentative automatique dans :',
      retryNow: 'Réessayer Maintenant',
      cancel: 'Annuler',
      helpText: 'Vous pouvez réessayer manuellement ou attendre la nouvelle tentative automatique. Le service sera restauré dès que possible.'
    }
  }
};
